import { ChangeEvent, useMemo, useState, useContext } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useRouter } from "src/hooks/useRouter";
import { maxLengthValidation } from "src/utils/validation";
import { notifySuccess } from "src/components/Common/Toast";
import { updateCashStatus, updateServiceName } from "src/service/pay";
import { useApiError } from "src/hooks/useApiError";
import { deleteService } from "src/service/cloud";
import { useCheckConsole } from "src/hooks/validation/useCheckConsole";
import useCompleteMsgModal from "src/hooks/Modal/useCompleteMsgModal";
import useGuideDownloadModal from "src/hooks/Modal/useGuideDownloadModal";
import { useGetMyServiceList } from "src/hooks/api/product/useGetMyServiceList";
import { ModalsDispatchContext } from "src/context/Modal/Console/ConsoleModal.context";
import { useGetCashStatus } from "src/hooks/api/pay/useGetCashStatus";
import { useGetPayUnregisterBill } from "src/hooks/api/bill/useGetPayUnregisterBill";
import { useGetPromotionUnregisterBill } from "src/hooks/api/bill/useGetPromotionUnregisterBill";
import { useSiteNxacList } from "src/hooks/api/nxac/useSiteNxacList";
import { useTranslation } from "react-i18next";
import { IGetCashUserStatusRes } from "src/service/types/pay.type";
import { IGetMyServiceListRes } from "src/service/types/console.type";
import { IGetSiteNxacListRes } from "src/service/types/cloud.type";

// ── 도메인별 슬라이스 타입 ────────────────────────────────
// 콘솔 화면을 view/site/service/cash 4개 관심사로 나눠 컨테이너가 그룹 단위로 전달한다.
export interface ConsoleViewSlice {
  serviceList: IGetMyServiceListRes[];
  siteNxacList: IGetSiteNxacListRes[];
  detailIndex: number;
  viewType: string;
  makeType: string;
  serviceNotice: string;
  createPercent: number;
  isUnregister: boolean; // 생성 안된 사이트 여부
  isDone: boolean; // 초기화 완료 여부
  closeCreateMsg: () => void;
}
export interface ConsoleSiteSlice {
  newSite: () => void;
  makePaySite: () => void;
  makePromotionSite: () => void;
  openEco: () => void;
  openEcoAdmin: () => void;
}
export interface ConsoleServiceSlice {
  modal: { confirmServiceNameIsActive: boolean; confirmDeleteIsActive: boolean };
  newServiceName: string;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  toggleModalConfirmServerName: () => void;
  toggleModalConfirmDelete: () => void;
  confirmServiceName: () => void;
  confirmServiceDelete: () => void;
}
export interface ConsoleCashSlice {
  userCashStatus?: IGetCashUserStatusRes;
  showCashReqModal: () => void;
  requestCashStatus: (status: string) => Promise<void>;
}
export interface ConsoleController {
  view: ConsoleViewSlice;
  site: ConsoleSiteSlice;
  service: ConsoleServiceSlice;
  cash: ConsoleCashSlice;
}

// ── 컨테이너 비즈니스 로직: 상태/쿼리/뮤테이션/핸들러를 한곳에 모아 슬라이스로 반환 ──
// UI(ConsoleUI)는 이 값을 그룹 props로 받아 소비한다.
export function useConsoleController(): ConsoleController {
  // serviceList가 2개 이상이면 list뷰 고려 (현재는 eCo의 경우에만 해당)
  const [viewType] = useState<string>("detail");
  const [modal, setModal] = useState({
    confirmServiceNameIsActive: false,
    confirmDeleteIsActive: false,
  });
  const [makeType, setMakeType] = useState<string>("");
  const [isDone, setIsDone] = useState<boolean>(false);
  const [serviceNotice, setServiceNotice] = useState<string>("");
  const [createPercent, setCreatePercent] = useState<number>(0);
  const [, setShowCashRequestDel] = useState<boolean>(false);
  const [newServiceName, setNewServiceName] = useState<string>("");
  const [deleteCashReqMsg, setDeleteCashReqMsg] = useState<string>("");

  const { setShowSiteNewModal, setServiceBillUid } = useContext(
    ModalsDispatchContext
  );
  const { setShowMsgModal } = useCompleteMsgModal();
  const { isMyServiceLoading, serviceList } = useGetMyServiceList({});
  const { isLoading: unregisterPayLoading, unregisterPayList } =
    useGetPayUnregisterBill({
      onSuccess: (data) => {
        if (data?.length > 0) {
          setServiceBillUid(data?.[0]?.serviceBillUid ?? "");
        }
      },
    });
  const { isLoading: unregisterPromotionLoading, unregisterPromotionList } =
    useGetPromotionUnregisterBill({
      onSuccess: (data) => {
        if (data?.length > 0) {
          setServiceBillUid(data?.[0]?.serviceBillUid ?? "");
        }
      },
    });
  const { userCashStatus, userCashStatusRefetch } = useGetCashStatus({
    onSuccess: () => {},
  });
  const { siteNxacList } = useSiteNxacList({
    serviceUid: serviceList ? serviceList[0]?.serviceUid : "",
  });
  const queryClient = useQueryClient();
  const { detailIndex, setDoNotOpenGuide, goEcoAdmin } =
    useGuideDownloadModal();
  const { openEco, goSiteType, goConsole } = useRouter();
  const { handleError } = useApiError();
  const { t } = useTranslation();

  const { mutateAsync: mutateUpdateServiceName } = useMutation(
    "updateServiceName",
    updateServiceName,
    {
      onSuccess: () => {
        toggleModalConfirmServerName();
      },
      onError: handleError,
    }
  );
  const { mutateAsync: mutateDeleteService } = useMutation(
    "deleteService",
    deleteService,
    {
      onSuccess: () => {
        goConsole();
      },
      onError: handleError,
    }
  );

  // useCheckConsole 의 useEffect 가 매 렌더마다 새 배열 참조로 재실행되지 않도록 안정화
  const unregisterPay = useMemo(
    () => unregisterPayList ?? [],
    [unregisterPayList]
  );
  const unregisterPromotion = useMemo(
    () => unregisterPromotionList ?? [],
    [unregisterPromotionList]
  );
  useCheckConsole({
    isMyServiceLoading:
      isMyServiceLoading || unregisterPayLoading || unregisterPromotionLoading,
    unregisterPay,
    unregisterPromotion,
    serviceList,
    setShowSiteNewModal,
    setMakeType,
    setDoNotOpenGuide,
    setServiceNotice,
    setCreatePercent,
    detailIndex,
    setShowMsgModal,
    setIsDone,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    switch (e.target?.name) {
      case "deleteCashReqMsg":
        setDeleteCashReqMsg(e.target.value);
        break;
      case "newServiceName":
        setNewServiceName(e.target.value);
        break;
    }
  };

  // 사이트 만들기 모달 열기
  const newSite = () => {
    setShowSiteNewModal(true);
  };

  function toggleModalConfirmServerName() {
    setModal((prev) => ({
      ...prev,
      confirmServiceNameIsActive: !prev.confirmServiceNameIsActive,
    }));
    setNewServiceName("");
  }

  const toggleModalConfirmDelete = () => {
    setModal((prev) => ({
      ...prev,
      confirmDeleteIsActive: !prev.confirmDeleteIsActive,
    }));
  };

  const confirmServiceName = () => {
    // 사이트 이름 최대 길이 검증은 yup검증으로 대체
    maxLengthValidation(newServiceName, 50);
    mutateUpdateServiceName({
      serviceUid: serviceList ? serviceList[detailIndex]?.serviceUid : "",
      serviceName: newServiceName,
    }).then(() => {
      queryClient.invalidateQueries("getMyServiceList");
    });
  };

  const confirmServiceDelete = () => {
    mutateDeleteService({
      serviceUid: serviceList ? serviceList[detailIndex]?.serviceUid : "",
    });
  };

  const makePaySite = () => {
    setShowSiteNewModal(true);
  };

  const makePromotionSite = () => {
    setShowSiteNewModal(true);
  };

  const showCashReqModal = () => {
    setShowCashRequestDel(true);
  };

  const closeCreateMsg = () => {
    setCreatePercent(0);
    setServiceNotice("");
  };

  const openEcoAction = () =>
    openEco({ domainName: serviceList?.[0]?.domainName ?? "" });
  const openEcoAdminAction = () =>
    goEcoAdmin({ domainName: serviceList?.[0]?.domainName ?? "" });

  const requestCashStatus = async (status: string) => {
    if (userCashStatus) {
      const param = {
        serviceCashUid: userCashStatus.serviceCashUid,
        status,
        msg: deleteCashReqMsg,
      };
      await updateCashStatus(param).then((res) => {
        if (res.status === "CANCEL") {
          notifySuccess(t("cashStatus.removeSuccess"));
          setIsDone(false);
          setDeleteCashReqMsg("");
          userCashStatusRefetch();
          setShowCashRequestDel(false);
          goSiteType();
        } else if (res.status === "APPLY") {
          notifySuccess(t("cashStatus.requestNotic"));
          userCashStatusRefetch();
          goConsole();
        }
      });
    }
  };

  return {
    view: {
      serviceList: serviceList ?? [],
      siteNxacList: siteNxacList ?? [],
      detailIndex,
      viewType,
      makeType,
      serviceNotice,
      createPercent,
      isUnregister:
        // 일반 & 프리미엄 중 하나라도 사이트 등록 안된것이 있는 경우
        unregisterPayList?.length !== 0 ||
        unregisterPromotionList?.length !== 0,
      isDone,
      closeCreateMsg,
    },
    site: {
      newSite,
      makePaySite,
      makePromotionSite,
      openEco: openEcoAction,
      openEcoAdmin: openEcoAdminAction,
    },
    service: {
      modal,
      newServiceName,
      handleChange,
      toggleModalConfirmServerName,
      toggleModalConfirmDelete,
      confirmServiceName,
      confirmServiceDelete,
    },
    cash: {
      userCashStatus,
      showCashReqModal,
      requestCashStatus,
    },
  };
}
